import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Bell, Search, Download, FileText, Users, Crown, AlertTriangle, UserPlus, CreditCard, Flag, Home, CheckCheck, Trash2, FileSpreadsheet, Loader2, Send, Mail } from "lucide-react";

/* ---------------- KPI extra (Overview) ---------------- */
const Trend = ({ cur, prev, suffix = "" }) => {
    if (prev === undefined || prev === null) return null;
    const diff = cur - prev;
    if (diff === 0) return <span className="text-ink-400">= periodo prec.{suffix}</span>;
    return <span className={diff > 0 ? "text-green-400" : "text-red-400"}>{diff > 0 ? "+" : ""}{diff} vs prec.{suffix}</span>;
};

const KpiX = ({ icon, label, value, sub, testid }) => (
    <div data-testid={testid} className="border border-white/10 p-5 bg-ink-900/40">
        <div className="flex items-center gap-2 text-xs text-gold tracking-[0.2em] uppercase">{icon} {label}</div>
        <div className="font-serif text-3xl text-ink-50 mt-3">{value}</div>
        {sub && <div className="text-xs text-ink-400 mt-1">{sub}</div>}
    </div>
);

export const ExtraKpis = () => {
    const [k, setK] = useState(null);
    useEffect(() => {
        const load = () => api.get("/admin/extra-kpi").then((r) => setK(r.data)).catch(() => { /* noop */ });
        load();
        const interval = window.setInterval(load, 60000);
        return () => window.clearInterval(interval);
    }, []);
    if (!k) return null;
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="extra-kpi-grid">
            <KpiX icon={<UserPlus size={14} />} label="Nuovi oggi" value={k.new_today} sub={<>ieri: {k.new_yesterday} · <Trend cur={k.new_today} prev={k.new_yesterday} /></>} testid="kpi-new-today" />
            <KpiX icon={<Users size={14} />} label="Nuovi 30gg" value={k.new_30d} sub={<Trend cur={k.new_30d} prev={k.new_prev30d} suffix=" 30gg" />} testid="kpi-new-30d" />
            <KpiX icon={<Crown size={14} />} label="Abbonati attivi" value={k.active_subs} sub={<>{k.paid_subs || 0} paganti · {k.trial_subs || 0} in prova</>} testid="kpi-active-subs" />
            <KpiX icon={<AlertTriangle size={14} />} label="Scadenza 7gg" value={k.expiring_7d} sub={`${k.expired_last_7d} scaduti negli ultimi 7gg`} testid="kpi-expiring-7d" />
        </div>
    );
};

/* ---------------- Centro controllo scadenze e feedback ---------------- */
const CENTER_CARDS = [
    { key: "trial_expiring", label: "Trial in scadenza", icon: <Crown size={16} />, cls: "text-amber-300" },
    { key: "payment_errors", label: "Errori Pagamento", icon: <CreditCard size={16} />, cls: "text-red-300" },
    { key: "negative_feedback", label: "Feedback Negativi", icon: <Flag size={16} />, cls: "text-orange-300" },
];

const CenterDetailModal = ({ which, data, onClose, onOpenFeedbackEmail }) => {
    const items = data?.[which]?.items || [];
    const title = CENTER_CARDS.find((c) => c.key === which)?.label;
    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div data-testid="expiry-center-modal" className="feature-card-neu w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <span className="label-eyebrow text-gold">{title} ({items.length})</span>
                    <button data-testid="expiry-center-modal-close" onClick={onClose} className="text-ink-400 hover:text-gold text-lg">✕</button>
                </div>
                {items.length === 0 ? (
                    <p className="text-sm text-ink-400">Nessun elemento da mostrare.</p>
                ) : (
                    <div className="space-y-2">
                        {items.map((it, i) => {
                            const isFeedback = which === "negative_feedback";
                            const Wrapper = isFeedback ? "button" : "div";
                            return (
                                <Wrapper key={it.user_id || it.id || i}
                                    data-testid={isFeedback ? `feedback-row-${it.id}` : undefined}
                                    onClick={isFeedback ? () => onOpenFeedbackEmail(it) : undefined}
                                    className={`w-full p-3 border border-white/10 bg-black/20 text-sm flex items-center justify-between gap-3 text-left ${isFeedback ? "vd-card hover:-translate-y-0.5 transition-transform cursor-pointer" : ""}`}>
                                    {which === "trial_expiring" && (
                                        <>
                                            <div><div className="text-ink-50">{it.agency_name || it.name}</div><div className="text-xs text-ink-400">{it.email}</div></div>
                                            <span className="text-amber-300 text-xs shrink-0">{it.days_left} giorni</span>
                                        </>
                                    )}
                                    {which === "payment_errors" && (
                                        <>
                                            <div><div className="text-ink-50">{it.user_email}</div><div className="text-xs text-ink-400">{it.plan_id} · {it.payment_status}</div></div>
                                            <span className="text-red-300 text-xs shrink-0">{new Date(it.created_at).toLocaleDateString("it-IT")}</span>
                                        </>
                                    )}
                                    {isFeedback && (
                                        <>
                                            <div><div className="text-ink-50">{it.user_name || it.user_email}</div><div className="text-xs text-ink-400">{it.role}</div></div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                {it.handled_at ? (
                                                    <span data-testid={`feedback-handled-badge-${it.id}`} className="text-[10px] text-green-400 flex items-center gap-1"><CheckCheck size={12} /> Preso in carico</span>
                                                ) : (
                                                    <span className="text-[10px] text-gold flex items-center gap-1"><Mail size={12} /> Contatta</span>
                                                )}
                                                <span className="text-xl">{it.value === "bad" ? "😞" : "😐"}</span>
                                            </div>
                                        </>
                                    )}
                                </Wrapper>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ---------------- Modale email IA per feedback negativi ---------------- */
const FeedbackEmailModal = ({ feedback, onClose, onSent }) => {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        api.post(`/admin/feedback/${feedback.id}/generate-email`)
            .then((r) => { setSubject(r.data.subject); setBody(r.data.body); })
            .catch(() => {
                setSubject("Il tuo feedback su vendidirect.it");
                setBody("<p>Ciao, abbiamo notato il tuo feedback e vorremmo capire come migliorare la tua esperienza.</p>");
            })
            .finally(() => setLoading(false));
    }, [feedback.id]);

    const send = async () => {
        setSending(true);
        try {
            await api.post(`/admin/feedback/${feedback.id}/send-email`, { subject, body });
            setSent(true);
            onSent && onSent(feedback.id);
        } catch (e) { /* noop */ }
        finally { setSending(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={onClose}>
            <div data-testid="feedback-email-modal" className="vd-card vd-kpi-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <span className="label-eyebrow text-gold flex items-center gap-1.5"><Mail size={12} /> Email di follow-up · IA</span>
                        <div className="text-sm text-ink-50 mt-1">{feedback.user_name || feedback.user_email}</div>
                    </div>
                    <button data-testid="feedback-email-close" onClick={onClose} className="text-ink-400 hover:text-gold text-lg">✕</button>
                </div>
                {loading ? (
                    <div className="flex items-center gap-2 text-ink-400 text-sm py-8 justify-center"><Loader2 size={16} className="animate-spin" /> Generazione bozza IA...</div>
                ) : sent ? (
                    <div data-testid="feedback-email-sent-confirm" className="text-center py-8 text-green-400 text-sm flex flex-col items-center gap-2">
                        <CheckCheck size={24} /> Email inviata e feedback preso in carico.
                    </div>
                ) : (
                    <>
                        <label className="block text-xs text-ink-400 mb-1">Oggetto</label>
                        <input data-testid="feedback-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
                            className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm mb-3" />
                        <label className="block text-xs text-ink-400 mb-1">Messaggio</label>
                        <textarea data-testid="feedback-email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                            className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-ink-50 focus:border-gold outline-none text-sm mb-4 leading-relaxed" />
                        <button data-testid="feedback-email-send" onClick={send} disabled={sending}
                            className="cta-gold w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                            <Send size={14} /> {sending ? "Invio in corso..." : "Invia tramite Resend"}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export const ExpiryFeedbackCenter = () => {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [emailFeedback, setEmailFeedback] = useState(null);
    useEffect(() => {
        const load = () => api.get("/admin/expiry-feedback-center").then((r) => setData(r.data)).catch(() => { /* noop */ });
        load();
        const interval = window.setInterval(load, 60000);
        return () => window.clearInterval(interval);
    }, []);
    if (!data) return null;
    const markHandled = (feedbackId) => {
        setData((d) => ({
            ...d,
            negative_feedback: {
                ...d.negative_feedback,
                items: d.negative_feedback.items.map((it) => (it.id === feedbackId ? { ...it, handled_at: new Date().toISOString() } : it)),
            },
        }));
    };
    return (
        <div className="mt-10" data-testid="expiry-feedback-center">
            <div className="label-eyebrow mb-4">Centro controllo scadenze e feedback</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                {CENTER_CARDS.map((c) => (
                    <button key={c.key} data-testid={`expiry-center-${c.key}`} onClick={() => setOpen(c.key)}
                        className="feature-card-neu p-5 text-left hover:-translate-y-0.5 transition-transform">
                        <div className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] ${c.cls}`}>{c.icon} {c.label}</div>
                        <div className="font-serif text-3xl text-ink-50 mt-3">{data[c.key]?.count ?? 0}</div>
                        <div className="text-[11px] text-ink-400 mt-1">Clicca per la lista dettagliata</div>
                    </button>
                ))}
            </div>
            {open && <CenterDetailModal which={open} data={data} onClose={() => setOpen(null)} onOpenFeedbackEmail={setEmailFeedback} />}
            {emailFeedback && <FeedbackEmailModal feedback={emailFeedback} onClose={() => setEmailFeedback(null)} onSent={markHandled} />}
        </div>
    );
};

/* ---------------- Centro notifiche ---------------- */
const NOTIF_ICONS = {
    new_user: <UserPlus size={14} className="text-gold" />,
    subscription: <Crown size={14} className="text-green-400" />,
    payment_failed: <CreditCard size={14} className="text-red-400" />,
    report: <Flag size={14} className="text-amber-400" />,
};
const NOTIF_FILTERS = [
    { id: "all", label: "Tutte" },
    { id: "new_user", label: "Utenti" },
    { id: "subscription", label: "Abbonamenti" },
    { id: "payment_failed", label: "Pagamenti" },
    { id: "report", label: "Segnalazioni" },
];

export const NotificationBell = () => {
    const [open, setOpen] = useState(false);
    const [filter, setFilter] = useState("all");
    const [data, setData] = useState({ items: [], unread: 0 });
    const ref = useRef(null);

    const load = useCallback((f = filter) => {
        api.get("/admin/notifications", { params: { filter: f } }).then((r) => setData(r.data)).catch(() => { /* noop */ });
    }, [filter]);

    useEffect(() => { load(filter); }, [filter, load]);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const markRead = async (n) => {
        if (n.read) return;
        await api.post(`/admin/notifications/${encodeURIComponent(n.id)}/read`);
        load();
    };
    const markAll = async () => {
        const ids = data.items.filter((i) => !i.read).map((i) => i.id);
        if (!ids.length) return;
        await api.post("/admin/notifications-read-all", { ids });
        load();
    };
    const remove = async (n, e) => {
        e.stopPropagation();
        await api.delete(`/admin/notifications/${encodeURIComponent(n.id)}`);
        load();
    };

    return (
        <div className="relative" ref={ref}>
            <button data-testid="admin-notif-bell" onClick={() => setOpen(!open)} className="relative p-2.5 border border-white/15 text-ink-100 hover:border-gold hover:text-gold transition-colors">
                <Bell size={16} />
                {data.unread > 0 && (
                    <span data-testid="admin-notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-gold text-ink-950 text-[10px] font-bold flex items-center justify-center rounded-full">
                        {data.unread > 99 ? "99+" : data.unread}
                    </span>
                )}
            </button>
            {open && (
                <div data-testid="admin-notif-dropdown" className="absolute right-0 top-full mt-2 w-[380px] max-w-[90vw] bg-ink-950 border border-gold/30 shadow-2xl z-50">
                    <div className="flex items-center justify-between p-4 border-b border-white/10">
                        <span className="label-eyebrow text-gold">Centro notifiche</span>
                        <button data-testid="admin-notif-read-all" onClick={markAll} className="text-xs text-ink-400 hover:text-gold flex items-center gap-1.5">
                            <CheckCheck size={12} /> Segna tutte lette
                        </button>
                    </div>
                    <div className="flex gap-1 p-3 border-b border-white/10 overflow-x-auto">
                        {NOTIF_FILTERS.map((f) => (
                            <button key={f.id} data-testid={`notif-filter-${f.id}`} onClick={() => setFilter(f.id)} className={`px-2.5 py-1 text-[11px] whitespace-nowrap border transition-colors ${filter === f.id ? "border-gold text-gold bg-gold/5" : "border-white/15 text-ink-400 hover:border-white/30"}`}>
                                {f.label}
                            </button>
                        ))}
                    </div>
                    <div className="max-h-[380px] overflow-y-auto divide-y divide-white/5">
                        {data.items.length === 0 ? (
                            <p className="p-6 text-center text-sm text-ink-400">Nessuna notifica.</p>
                        ) : data.items.map((n) => (
                            <div key={n.id} data-testid={`notif-item-${n.type}`} onClick={() => markRead(n)} className={`p-3.5 flex items-start gap-3 cursor-pointer hover:bg-white/5 group ${n.read ? "opacity-50" : ""}`}>
                                <div className="mt-0.5 shrink-0">{NOTIF_ICONS[n.type]}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink-50 flex items-center gap-2">
                                        {n.title}
                                        {!n.read && <span className="w-1.5 h-1.5 bg-gold rounded-full shrink-0" />}
                                    </div>
                                    <div className="text-xs text-ink-400 truncate mt-0.5">{n.detail}</div>
                                    <div className="text-[10px] text-ink-400/70 mt-1">{new Date(n.at).toLocaleString("it-IT")}</div>
                                </div>
                                <button onClick={(e) => remove(n, e)} className="opacity-0 group-hover:opacity-100 p-1 text-ink-400 hover:text-red-400 transition-opacity shrink-0" title="Elimina">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ---------------- Ricerca globale ---------------- */
export const GlobalSearch = ({ onOpenTab }) => {
    const [q, setQ] = useState("");
    const [res, setRes] = useState(null);
    const [busy, setBusy] = useState(false);
    const ref = useRef(null);
    const timer = useRef(null);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setRes(null); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const onChange = (val) => {
        setQ(val);
        clearTimeout(timer.current);
        if (val.trim().length < 2) { setRes(null); return; }
        timer.current = setTimeout(async () => {
            setBusy(true);
            try {
                const r = await api.get("/admin/search", { params: { q: val.trim() } });
                setRes(r.data);
            } catch (e) { /* noop */ }
            finally { setBusy(false); }
        }, 300);
    };

    const empty = res && !res.users?.length && !res.properties?.length && !res.invoices?.length;
    const Section = ({ title, children }) => (
        <div className="py-1">
            <div className="px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] text-gold">{title}</div>
            {children}
        </div>
    );

    return (
        <div className="relative flex-1 max-w-xl" ref={ref}>
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            {busy && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gold animate-spin" />}
            <input
                data-testid="admin-global-search"
                value={q}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Ricerca globale: utenti, immobili, fatture..."
                className="w-full pl-10 pr-9 py-2.5 bg-transparent border border-white/15 text-ink-50 focus:border-gold outline-none text-sm"
            />
            {res && (
                <div data-testid="global-search-results" className="absolute left-0 right-0 top-full mt-2 bg-ink-950 border border-gold/30 shadow-2xl z-50 max-h-[420px] overflow-y-auto divide-y divide-white/10">
                    {empty && <p className="p-5 text-center text-sm text-ink-400">Nessun risultato per «{q}»</p>}
                    {res.users?.length > 0 && (
                        <Section title="Utenti">
                            {res.users.map((u) => (
                                <button key={u.user_id} data-testid={`gsearch-user-${u.user_id}`} onClick={() => { onOpenTab("users", u.email); setRes(null); }} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-white/5 text-sm">
                                    <Users size={13} className="text-ink-400 shrink-0" />
                                    <span className="text-ink-50 truncate">{u.name || u.email}</span>
                                    <span className="text-xs text-ink-400 truncate">{u.email}</span>
                                    {u.plan_id && <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-gold/20 text-gold shrink-0">{u.plan_id}</span>}
                                </button>
                            ))}
                        </Section>
                    )}
                    {res.properties?.length > 0 && (
                        <Section title="Immobili">
                            {res.properties.map((p) => (
                                <Link key={p.id} data-testid={`gsearch-prop-${p.id}`} to={`/annunci/${p.id}`} onClick={() => setRes(null)} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-white/5 text-sm">
                                    <Home size={13} className="text-ink-400 shrink-0" />
                                    <span className="text-ink-50 truncate">{p.title}</span>
                                    <span className="text-xs text-ink-400">{p.city}</span>
                                    <span className="ml-auto text-price font-serif text-xs shrink-0">{formatPrice(p.price)}</span>
                                </Link>
                            ))}
                        </Section>
                    )}
                    {res.invoices?.length > 0 && (
                        <Section title="Fatture">
                            {res.invoices.map((i) => (
                                <button key={i.id} data-testid={`gsearch-inv-${i.id}`} onClick={() => { onOpenTab("invoices"); setRes(null); }} className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-white/5 text-sm">
                                    <FileText size={13} className="text-ink-400 shrink-0" />
                                    <span className="text-ink-50">{i.invoice_number || i.number}</span>
                                    <span className="text-xs text-ink-400 truncate">{i.customer_name}</span>
                                    {i.total != null && <span className="ml-auto text-price font-serif text-xs shrink-0">{formatPrice(i.total)}</span>}
                                </button>
                            ))}
                        </Section>
                    )}
                </div>
            )}
        </div>
    );
};

/* ---------------- Export multi-formato ---------------- */
const FMTS = [
    { fmt: "pdf", label: "PDF", icon: <FileText size={13} /> },
    { fmt: "csv", label: "CSV", icon: <Download size={13} /> },
    { fmt: "xlsx", label: "Excel", icon: <FileSpreadsheet size={13} /> },
];

export const ExportButtons = ({ entity }) => (
    <div className="flex gap-2 items-center">
        <span className="text-xs text-ink-400 uppercase tracking-wider mr-1">Esporta</span>
        {FMTS.map((f) => (
            <button
                key={f.fmt}
                data-testid={`export-${entity}-${f.fmt}`}
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export2/${entity}.${f.fmt}`, "_blank")}
                className="cta-outline px-3.5 py-2 text-xs flex items-center gap-1.5"
            >
                {f.icon} {f.label}
            </button>
        ))}
    </div>
);
